// Bulk check screen (spec §7.1.2): load a CSV/JSON, run in chunks with progress and Stop,
// stat tiles as filters, filter bar, virtualized table, detail panel, chunked exports.
// Basecoat empty state, cards, alerts, progress, native selects, input group and dropdown menu.
import {
  detailedCsv, isErrorField, jsonReport, parseInput, summaryCsv, textReport, toPage as pageOf, InputError,
  type Checker, type Config, type FieldResult, type InputRow, type PageResult, type Profile, type Status, type WarningCode,
} from '../engine';
import { alert, btn, clear, downloadBlob, fmt, h, icon, ICONS } from './dom';
import { badge, deviceFrame, warningChip, warningLabel } from './preview';
import { CLASS_LABEL, CLASS_ORDER, enabledProfiles, onSettings, profileSet, state } from './state';
import { track } from './track';

type St = Status | 'error';
type Source = 'file' | 'paste' | 'sample' | 'rerun';
interface Lean { overall: Status; st: Status[] }
interface PageRow {
  i: number; url: string; error?: string;
  t?: Lean; d?: Lean;
  warns: WarningCode[];
  warnDetail: Map<WarningCode, string>;
  worst: St;
  text: string; // lowercased url + title + description, for search
}
interface Run { rows: InputRow[]; name: string; profiles: Profile[]; checker: Checker; config: Config; brand: string; pages: PageRow[]; done: boolean; stopped: boolean; ms: number }

const RANK: Record<St, number> = { error: 4, cut: 3, tight: 2, fits: 1 };
const CHUNK = 250;
const ROW_H = 44;
const OVERSCAN = 8;

const lean = (f: FieldResult): Lean => ({ overall: f.overall as Status, st: f.results.map(r => r.status) });
const yieldToBrowser = () => new Promise<void>(r => setTimeout(r, 0));

export function bulkView(): HTMLElement {
  let run: Run | null = null;
  let token = 0;
  const filter = { q: '', status: '' as '' | St, field: '' as '' | 'title' | 'description', warns: new Set<WarningCode>(), tile: '', sort: 'row' };
  let view: PageRow[] = [];

  const root = h('div', { class: 'bulk' },
    h('div', { class: 'page-intro' },
      h('p', { class: 'eyebrow' }, 'Title & Meta Description Length Checker'),
      h('h1', null, 'Bulk Check Thousands of URLs'),
      h('p', null, 'Check the titles and meta descriptions of a whole site at once. Drop in a spreadsheet of pages and see which ones Google cuts, on which devices, and which words are lost.')));
  const top = h('div');
  const results = h('div', { class: 'bulk', hidden: true });
  root.append(top, results);

  // ---------- empty state ----------
  const fileInput = h('input', { type: 'file', accept: '.csv,.json,.ndjson,text/csv,application/json', hidden: true, 'aria-label': 'Choose a CSV or JSON file' });
  fileInput.addEventListener('change', async () => {
    const f = fileInput.files?.[0];
    if (f) start(await f.text(), f.name, 'file');
    fileInput.value = '';
  });
  const errorBox = h('div');
  const pasteArea = h('textarea', { id: 'paste-input', placeholder: 'url,title,description\nhttps://example.com/,Example title,Example description', spellcheck: false });
  const paste = h('section', { class: 'card paste', hidden: true, 'aria-labelledby': 'paste-title' },
    h('header', null, h('h2', { id: 'paste-title' }, 'Paste CSV or JSON'), h('p', null, 'One row per page, with a header row.')),
    h('section', null, h('div', { class: 'field' }, h('label', { for: 'paste-input', class: 'sr-only' }, 'CSV or JSON to check'), pasteArea)),
    h('footer', null,
      h('button', { ...btn('ghost'), onclick: () => { paste.hidden = true; } }, 'Cancel'),
      h('button', { ...btn(), onclick: () => start(pasteArea.value, 'pasted.csv', 'paste') }, 'Check rows')));

  const drop = h('div', { class: 'empty drop' },
    h('header', null,
      h('figure', null, icon(ICONS.upload, 20)),
      h('h2', null, 'Drop a CSV or JSON file'),
      h('p', null, 'CSV, JSON or NDJSON. Files never leave your browser.')),
    h('footer', null,
      h('button', { ...btn(), onclick: () => fileInput.click() }, 'Choose file'),
      h('div', { class: 'drop-links' },
        h('button', { class: 'linkish', type: 'button', onclick: () => { paste.hidden = false; pasteArea.focus(); } }, 'Paste CSV'),
        h('button', { class: 'linkish', type: 'button', onclick: loadSample }, 'Try the sample file'))),
    fileInput);
  drop.addEventListener('dragover', e => { e.preventDefault(); drop.classList.add('over'); });
  drop.addEventListener('dragleave', () => drop.classList.remove('over'));
  drop.addEventListener('drop', async e => {
    e.preventDefault(); drop.classList.remove('over');
    const f = e.dataTransfer?.files?.[0];
    if (f) start(await f.text(), f.name, 'file');
  });
  const code = (t: string) => h('code', null, t);
  const COLUMNS: [string, string, string][] = [
    ['url', 'Optional', 'Names the row in the results and exports.'],
    ['title', 'Title or description', 'The title tag to check.'],
    ['description', 'Title or description', 'The meta description to check.'],
    ['brand', 'Optional', 'Warns when the brand is cut. Rows without one use the default brand from Settings.'],
    ['target_keyword', 'Optional', 'Warns when this keyword is cut.'],
    ['query', 'Optional', 'The search someone typed. Matching words in the description are bolded, as Google does, which makes them wider.'],
  ];
  const guide = h('div', { class: 'guide' },
    h('section', { class: 'card', 'aria-labelledby': 'b-cols' },
      h('header', null, h('h2', { id: 'b-cols' }, 'What to put in your file'),
        h('p', null, 'One row per page, with a header row. Every file needs a title or a description column; the rest are optional. Other columns are ignored.')),
      h('section', null,
      h('div', { class: 'col-list', role: 'table', 'aria-label': 'Columns' },
        COLUMNS.map(([name, need, what]) => h('div', { class: 'col-row', role: 'row' },
          h('span', { role: 'cell' }, code(name)),
          h('span', { role: 'cell', class: 'col-need' }, need),
          h('span', { role: 'cell' }, what)))),
      h('p', { class: 'hint' }, 'Crawler exports work as they are: ', code('Address'), ', ', code('Title 1'), ' and ', code('Meta Description 1'),
        ' from Screaming Frog are read as url, title and description. For JSON, use an array of objects with the same keys, or one object per line (NDJSON).'))),
    h('section', { class: 'card', 'aria-labelledby': 'b-how' },
      h('header', null, h('h2', { id: 'b-how' }, 'How a bulk check works')),
      h('section', null,
      h('ol', { class: 'steps' },
        h('li', null, h('strong', null, 'Load a file. '), 'Every row is checked on each device that’s on in ', h('a', { href: '/settings' }, 'Settings'), ', in your browser. You can stop at any time and keep the rows checked so far.'),
        h('li', null, h('strong', null, 'Find the problems. '), 'Click a count, like Title cut, to show only those rows. Search, filter by status, field or warning, or sort by worst status.'),
        h('li', null, h('strong', null, 'Look closer. '), 'Click any row to see its previews on every device, with the cut words marked.'),
        h('li', null, h('strong', null, 'Export. '), 'Download a detailed CSV (one row per page, field and device), a summary CSV (one row per page), JSON, or a text report to share.')))));
  const empty = h('div', { class: 'bulk' }, errorBox, drop, paste, guide);

  async function loadSample() {
    try {
      const res = await fetch('/sample.csv');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      start(await res.text(), 'sample.csv', 'sample');
    } catch (e) { showError(`Couldn’t load the sample file (${e instanceof Error ? e.message : e}).`); }
  }

  function showError(msg: string) {
    clear(errorBox, alert(msg, 'destructive'));
  }

  // ---------- running ----------
  const progressFill = h('span', { style: { width: '0%' } });
  const progressCount = h('span', { class: 'filerow-count mono' });
  const fileName = h('span', { class: 'filerow-name' });
  const stopBtn = h('button', btn('outline', 'sm'), 'Stop');
  const newBtn = h('button', btn('outline', 'sm'), 'New check');
  const rerunBtn = h('button', { ...btn('outline', 'sm'), hidden: true }, 'Re-run with current settings');
  const progress = h('div', { class: 'progress', role: 'progressbar', 'aria-label': 'Rows checked', 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': '0' }, progressFill);
  const fileRow = h('section', { class: 'card filerow', 'aria-label': 'File' },
    h('section', null,
      h('div', { style: { minWidth: '0', display: 'grid', gap: '2px' } }, fileName, progressCount),
      h('div', { class: 'filerow-actions' }, rerunBtn, stopBtn, newBtn),
      progress));
  stopBtn.addEventListener('click', () => { if (run) run.stopped = true; });
  newBtn.addEventListener('click', () => { token++; run = null; renderTop(); results.hidden = true; });
  rerunBtn.addEventListener('click', () => { if (run) begin(run.rows, run.name, 'rerun'); });

  function renderTop() {
    if (!run) { clear(top, empty); return; }
    clear(top, fileRow);
  }

  function start(text: string, name: string, source: Source) {
    clear(errorBox);
    let rows: InputRow[];
    try { rows = parseInput(text, name); } catch (e) {
      showError(e instanceof InputError ? e.message : `Couldn’t read that file: ${e instanceof Error ? e.message : e}`);
      return;
    }
    if (!rows.length) { showError('That file has a header but no rows.'); return; }
    paste.hidden = true;
    begin(rows, name, source);
  }

  async function begin(rows: InputRow[], name: string, source: Source) {
    const my = ++token;
    const profiles = enabledProfiles();
    run = { rows, name, profiles, checker: state.checker, config: state.config, brand: state.settings.brand, pages: [], done: false, stopped: false, ms: 0 };
    const r = run;
    renderTop();
    rerunBtn.hidden = true;
    stopBtn.hidden = false;
    clear(staleNote);
    fileName.textContent = name;
    results.hidden = false;
    const t0 = performance.now();
    for (let at = 0; at < rows.length; at += CHUNK) {
      if (my !== token) return;
      if (r.stopped) break;
      const end = Math.min(rows.length, at + CHUNK);
      for (let i = at; i < end; i++) r.pages.push(toPage(i, r.checker.checkPage(rows[i], i, profiles, r.brand || undefined)));
      const pct = Math.round((end / rows.length) * 100);
      progressFill.style.width = `${pct}%`;
      progress.setAttribute('aria-valuenow', String(pct));
      progressCount.textContent = `${fmt(end)} / ${fmt(rows.length)} rows`;
      if (at === 0 || end === rows.length || (at / CHUNK) % 8 === 0) renderResults();
      await yieldToBrowser();
    }
    if (my !== token) return;
    r.done = true;
    r.ms = performance.now() - t0;
    stopBtn.hidden = true;
    progressCount.textContent = r.stopped
      ? `Stopped at ${fmt(r.pages.length)} of ${fmt(rows.length)} rows`
      : `${fmt(rows.length)} rows · ${r.profiles.length} devices · ${(r.ms / 1000).toFixed(1)}s`;
    renderResults();
    track('bulk_check', { source, outcome: r.stopped ? 'stopped' : 'completed', row_count: r.pages.length, device_count: r.profiles.length });
  }

  function toPage(i: number, res: FieldResult[]): PageRow {
    const err = res.find(isErrorField);
    const url = res[0]?.url ?? `row-${i + 1}`;
    if (err) return { i, url, error: err.error ?? 'error', warns: [], warnDetail: new Map(), worst: 'error', text: `${url} ${err.error}`.toLowerCase() };
    const fr = res;
    const t = fr.find(r => r.field === 'title'), d = fr.find(r => r.field === 'description');
    const warnDetail = new Map<WarningCode, string>();
    for (const f of fr) for (const w of f.warnings) if (!warnDetail.has(w.code)) warnDetail.set(w.code, w.detail ?? '');
    const worst = [t?.overall, d?.overall].filter(Boolean).reduce<St>((a, b) => (RANK[b as St] > RANK[a] ? (b as St) : a), 'fits');
    return { i, url, t: t && lean(t), d: d && lean(d), warns: [...warnDetail.keys()], warnDetail, worst, text: `${url} ${t?.original ?? ''} ${d?.original ?? ''}`.toLowerCase() };
  }

  // ---------- results ----------
  const tiles = h('div', { class: 'tiles', role: 'group', 'aria-label': 'Result counts; click to filter' });
  const search = h('input', { type: 'search', placeholder: 'Search URL or text', 'aria-label': 'Search' });
  const searchGroup = h('div', { class: 'input-group search' }, search, h('span', { 'data-align': 'inline-start' }, icon(ICONS.search, 16)));
  search.addEventListener('input', () => { filter.q = search.value.trim().toLowerCase(); applyFilter(); });
  const statusSel = select('Status', [['', 'Any status'], ['cut', 'Cut'], ['tight', 'Tight'], ['fits', 'Fits'], ['error', 'Error']], v => { filter.status = v as any; filter.tile = ''; applyFilter(); });
  const fieldSel = select('Field', [['', 'Title or description'], ['title', 'Title'], ['description', 'Description']], v => { filter.field = v as any; filter.tile = ''; applyFilter(); });
  const sortSel = select('Sort', [['row', 'Sort: row'], ['url', 'Sort: URL'], ['status', 'Sort: worst status'], ['warnings', 'Sort: most warnings']], v => { filter.sort = v; applyFilter(); });
  const warnChips = h('div', { class: 'chips', role: 'group', 'aria-label': 'Filter by warning' });
  const exportMenu = menu();
  const countLine = h('p', { class: 'count-line', 'aria-live': 'polite' });
  const staleNote = h('div');
  const tableHost = h('div', { class: 'table' });
  const resultsHint = h('p', { class: 'hint' }, 'Click a count or a warning to filter the table. Click a row to see its previews.');
  results.append(staleNote, tiles, resultsHint,
    h('div', { class: 'filterbar' }, searchGroup, statusSel, fieldSel, sortSel, h('span', { class: 'spacer' }), exportMenu.el),
    warnChips, countLine, tableHost);

  function select(label: string, opts: [string, string][], on: (v: string) => void) {
    const s = h('select', { class: 'select', 'aria-label': label }, opts.map(([v, t]) => h('option', { value: v }, t)));
    s.addEventListener('change', () => on(s.value));
    return s;
  }

  function fieldStatus(p: PageRow, f: 'title' | 'description'): St | undefined {
    if (p.error) return 'error';
    return (f === 'title' ? p.t : p.d)?.overall;
  }

  function renderResults() {
    if (!run) return;
    const pages = run.pages;
    const count = (f: 'title' | 'description', s: Status) => pages.reduce((n, p) => n + (fieldStatus(p, f) === s ? 1 : 0), 0);
    const warned = pages.reduce((n, p) => n + (p.warns.length ? 1 : 0), 0);
    const errors = pages.reduce((n, p) => n + (p.error ? 1 : 0), 0);
    const defs: [string, string, number, string][] = [
      ['title:cut', 'Title cut', count('title', 'cut'), 'cut'], ['title:tight', 'Title tight', count('title', 'tight'), 'tight'], ['title:fits', 'Title fits', count('title', 'fits'), 'fits'],
      ['description:cut', 'Description cut', count('description', 'cut'), 'cut'], ['description:tight', 'Description tight', count('description', 'tight'), 'tight'], ['description:fits', 'Description fits', count('description', 'fits'), 'fits'],
      ['warn', 'With warnings', warned, 'warn'],
    ];
    if (errors) defs.push(['error', 'Errors', errors, 'warn']);
    clear(tiles, defs.map(([key, label, n, dot]) => {
      const b = h('button', { type: 'button', class: 'tile', 'aria-pressed': String(filter.tile === key) },
        h('span', { class: 'tile-k' }, h('i', { class: `tile-dot dot-${dot}`, 'aria-hidden': 'true' }), label),
        h('span', { class: 'tile-v' }, fmt(n)));
      b.addEventListener('click', () => {
        filter.tile = filter.tile === key ? '' : key;
        filter.status = ''; filter.field = '';
        statusSel.value = ''; fieldSel.value = '';
        renderResults();
      });
      return b;
    }));

    const present = new Map<WarningCode, number>();
    for (const p of pages) for (const w of p.warns) present.set(w, (present.get(w) ?? 0) + 1);
    clear(warnChips, [...present.entries()].map(([code, n]) => {
      const b = h('button', { type: 'button', class: 'chip', 'aria-pressed': String(filter.warns.has(code)) }, `${warningLabel({ code, detail: '' })} · ${fmt(n)}`);
      b.addEventListener('click', () => { if (filter.warns.has(code)) filter.warns.delete(code); else filter.warns.add(code); renderResults(); });
      return b;
    }));
    exportMenu.setEnabled(run.done);
    applyFilter();
  }

  function applyFilter() {
    if (!run) return;
    const f = filter;
    let v = run.pages.filter(p => {
      if (f.q && !p.text.includes(f.q)) return false;
      if (f.warns.size && ![...f.warns].every(w => p.warns.includes(w))) return false;
      if (f.tile) {
        if (f.tile === 'warn') { if (!p.warns.length) return false; }
        else if (f.tile === 'error') { if (!p.error) return false; }
        else { const [fld, s] = f.tile.split(':'); if (fieldStatus(p, fld as any) !== s) return false; }
      }
      if (f.status) {
        const fields = f.field ? [f.field] : ['title', 'description'] as const;
        if (!fields.some(fl => fieldStatus(p, fl) === f.status)) return false;
      }
      return true;
    });
    if (f.sort === 'url') v = v.slice().sort((a, b) => a.url.localeCompare(b.url));
    else if (f.sort === 'status') v = v.slice().sort((a, b) => RANK[b.worst] - RANK[a.worst] || a.i - b.i);
    else if (f.sort === 'warnings') v = v.slice().sort((a, b) => b.warns.length - a.warns.length || a.i - b.i);
    view = v;
    countLine.textContent = `Showing ${fmt(v.length)} of ${fmt(run.pages.length)} pages${run.done ? '' : ' so far'}. Exports include every page.`;
    buildTable();
  }

  // ---------- virtualized table ----------
  const scroller = h('div', { class: 'table-scroll', tabindex: '-1' });
  const head = h('div', { class: 't-head', role: 'row' });
  const body = h('div', { class: 't-body', role: 'rowgroup' });
  scroller.append(head, body);
  tableHost.append(scroller);
  tableHost.setAttribute('role', 'table');
  tableHost.setAttribute('aria-label', 'Results');
  let compact = false;
  const mq = window.matchMedia('(max-width: 720px)');
  mq.addEventListener('change', () => buildTable());

  function pill(s: St | undefined, text?: string, title?: string) {
    if (!s) return h('span', { class: 'muted' }, '—');
    const word = { fits: 'Fits', tight: 'Tight', cut: 'Cut', error: 'Error' }[s];
    return h('span', { class: `pill pill-${s}`, title }, h('i', { class: 'dot', 'aria-hidden': 'true' }), text ?? word);
  }

  function aggregate(l: Lean | undefined, err?: string) {
    if (err) return pill('error');
    if (!l) return pill(undefined);
    const n = l.st.filter(s => s === l.overall).length;
    return pill(l.overall, `${{ fits: 'Fits', tight: 'Tight', cut: 'Cut' }[l.overall]} ${n}/${l.st.length}`);
  }

  function buildTable() {
    if (!run) return;
    const ps = run.profiles;
    compact = mq.matches || ps.length > 6;
    const cols = compact
      ? ['40px', 'minmax(0, 1fr)', '88px', '88px', '40px']
      : ['56px', 'minmax(220px, 2fr)', ...ps.map(() => '120px'), ...ps.map(() => '120px'), '88px'];
    tableHost.style.setProperty('--cols', cols.join(' '));
    tableHost.style.setProperty('--min-w', compact ? '0' : `${56 + 220 + ps.length * 240 + 88}px`);
    const sortBtn = (label: string, key: string) => {
      const b = h('button', { type: 'button', 'aria-label': `Sort by ${label}` }, label, filter.sort === key ? '↓' : '');
      b.addEventListener('click', () => { filter.sort = key; sortSel.value = key; applyFilter(); });
      return h('span', { role: 'columnheader' }, b);
    };
    const short = (p: Profile) => p.shortLabel ?? p.label;
    clear(head,
      sortBtn('#', 'row'), sortBtn('URL', 'url'),
      compact
        ? [h('span', { role: 'columnheader' }, 'Title'), h('span', { role: 'columnheader' }, 'Description')]
        : [...ps.map(p => h('span', { role: 'columnheader', title: `Title on ${p.label}` }, `T · ${short(p)}`)), ...ps.map(p => h('span', { role: 'columnheader', title: `Description on ${p.label}` }, `D · ${short(p)}`))],
      sortBtn(compact ? '⚠' : 'Warnings', 'warnings'));
    body.style.height = `${view.length * ROW_H}px`;
    drawn = '';
    drawRows();
  }

  let drawn = '';
  function drawRows() {
    if (!run) return;
    const top = Math.max(0, scroller.scrollTop - 36);
    const first = Math.max(0, Math.floor(top / ROW_H) - OVERSCAN);
    const last = Math.min(view.length, Math.ceil((top + scroller.clientHeight) / ROW_H) + OVERSCAN);
    const key = `${first}:${last}:${view.length}:${compact}`;
    if (key === drawn) return;
    drawn = key;
    const ps = run.profiles;
    const rows: HTMLElement[] = [];
    for (let k = first; k < last; k++) {
      const p = view[k];
      const cells = compact
        ? [aggregate(p.t, p.error), aggregate(p.d, p.error)]
        : p.error
          ? [h('span', { class: 'muted', style: { gridColumn: `span ${ps.length * 2}` } }, pill('error'), ' ', p.error)]
          : [...ps.map((pr, j) => p.t ? pill(p.t.st[j], undefined, `Title on ${pr.label}`) : pill(undefined)), ...ps.map((pr, j) => p.d ? pill(p.d.st[j], undefined, `Description on ${pr.label}`) : pill(undefined))];
      const row = h('div', { class: 't-row', role: 'row', tabindex: '0', style: { top: `${k * ROW_H}px` }, 'aria-label': `Row ${p.i + 1}, ${p.url}`, 'data-row': String(p.i) },
        h('span', { class: 't-num', role: 'cell' }, fmt(p.i + 1)),
        h('span', { class: 't-url', role: 'cell', title: p.url }, p.url),
        cells.map(c => { c.setAttribute('role', 'cell'); return c; }),
        h('span', { role: 'cell', class: 't-num', title: p.warns.map(w => warningLabel({ code: w, detail: p.warnDetail.get(w) ?? '' })).join(', ') }, p.warns.length ? fmt(p.warns.length) : ''));
      rows.push(row);
    }
    clear(body, rows);
  }
  scroller.addEventListener('scroll', () => requestAnimationFrame(drawRows), { passive: true });
  body.addEventListener('click', e => {
    const row = (e.target as HTMLElement).closest<HTMLElement>('.t-row');
    if (row) openDetail(Number(row.dataset.row));
  });
  body.addEventListener('keydown', e => {
    const row = (e.target as HTMLElement).closest<HTMLElement>('.t-row');
    if (!row) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetail(Number(row.dataset.row)); }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const idx = view.findIndex(p => p.i === Number(row.dataset.row)) + (e.key === 'ArrowDown' ? 1 : -1);
      if (idx < 0 || idx >= view.length) return;
      const y = idx * ROW_H;
      if (y < scroller.scrollTop || y + ROW_H * 2 > scroller.scrollTop + scroller.clientHeight) scroller.scrollTop = y - scroller.clientHeight / 2;
      drawRows();
      body.querySelector<HTMLElement>(`[data-row="${view[idx].i}"]`)?.focus();
    }
  });

  // ---------- detail panel ----------
  const dialog = h('dialog', { class: 'panel', 'aria-labelledby': 'panel-title' });
  document.body.append(dialog);
  dialog.addEventListener('click', e => { if (e.target === dialog) dialog.close(); });

  function openDetail(i: number) {
    if (!run) return;
    const row = run.rows[i];
    const res = run.checker.checkPage(row, i, run.profiles, run.brand || undefined);
    const err = res.find(isErrorField);
    const fr = res.filter(r => !isErrorField(r));
    const t = fr.find(r => r.field === 'title'), d = fr.find(r => r.field === 'description');
    const closeBtn = h('button', { ...btn('ghost', 'icon-sm'), 'aria-label': 'Close' }, icon(ICONS.close, 16));
    closeBtn.addEventListener('click', () => dialog.close());
    const brand = (row.brand ?? '').trim() || run.brand;
    const summary = (f: FieldResult | undefined, name: string) => f ? h('div', { class: 'sum-row' }, h('span', { class: 'sum-k' }, name),
      h('div', { class: 'sum-v' }, h('div', { class: 'sum-status' }, badge(f.overall)),
        f.warnings.filter(w => w.code !== 'TIGHT').length ? h('div', { class: 'chips' }, f.warnings.filter(w => w.code !== 'TIGHT').map(warningChip)) : null)) : null;
    const classes = CLASS_ORDER.filter(c => run!.profiles.some(p => p.deviceClass === c));
    clear(dialog,
      h('div', { class: 'panel-head' }, h('span', { class: 't-num' }, `Row ${fmt(i + 1)}`), h('h2', { id: 'panel-title', title: res[0]?.url }, res[0]?.url ?? ''), closeBtn),
      h('div', { class: 'panel-body' },
        err ? alert(err.error ?? 'error', 'destructive') : [
          h('section', { class: 'card summary', 'aria-label': 'Summary' }, h('section', null, summary(t, 'Title'), summary(d, 'Description'))),
          h('div', { class: 'canvas canvas-all' }, classes.map(c => h('section', { class: 'band', 'data-class': c },
            h('h3', { class: 'band-title' }, CLASS_LABEL[c]),
            h('div', { class: 'frames' }, run!.profiles.filter(p => p.deviceClass === c).map(p =>
              deviceFrame({ profile: p, title: t, description: d, url: row.url ?? '', brand, query: row.query ?? '', showHidden: state.settings.showHidden, config: run!.config })))))),
        ]));
    dialog.showModal();
    closeBtn.focus();
  }

  // ---------- exports ----------
  function* allPages(r: Run): Generator<PageResult> {
    for (let i = 0; i < r.pages.length; i++) yield pageOf(r.checker.checkPage(r.rows[i], i, r.profiles, r.brand || undefined), r.pages[i].url);
  }

  async function exportAs(kind: 'detailed' | 'summary' | 'json' | 'text') {
    if (!run) return;
    const r = run;
    const base = r.name.replace(/\.[^.]+$/, '') || 'serpsize';
    const ids = r.profiles.map(p => p.id);
    const runInfo = {
      serpsizeVersion: '0.1.0', generatedAt: new Date().toISOString(), measuredIn: navigator.userAgent,
      config: r.config, brand: r.brand || undefined,
      profiles: r.profiles.map(p => ({ id: p.id, status: p.status, verified: p.verified, engine: p.engine })),
    };
    const gen = kind === 'detailed' ? detailedCsv(allPages(r))
      : kind === 'summary' ? summaryCsv(allPages(r), ids)
      : kind === 'json' ? jsonReport(allPages(r), runInfo)
      : textReport(allPages(r), r.profiles, profileSet.byId, r.config);
    const parts: string[] = [];
    let n = 0;
    for (const chunk of gen) {
      parts.push(chunk);
      if (++n % 500 === 0) await yieldToBrowser();
    }
    const [ext, type] = kind === 'json' ? ['json', 'application/json'] : kind === 'text' ? ['txt', 'text/plain'] : ['csv', 'text/csv'];
    downloadBlob(parts, `${base}-${kind === 'detailed' ? 'detailed' : kind === 'summary' ? 'summary' : 'results'}.${ext}`, `${type};charset=utf-8`);
    track('bulk_export', { export_format: kind, row_count: r.pages.length });
  }

  // Basecoat dropdown menu: its script opens and closes it, and handles arrow keys, Home / End,
  // Enter and Escape (focus stays on the trigger; the active item is aria-activedescendant).
  function menu() {
    const trigger = h('button', { ...btn('outline'), id: 'export-trigger', 'aria-haspopup': 'menu', 'aria-controls': 'export-menu', 'aria-expanded': 'false' }, icon(ICONS.download, 14), 'Export');
    const items: [typeof exportAs extends (k: infer K) => any ? K : never, string, string][] = [
      ['detailed', 'Detailed CSV', 'One row per page, field and device'],
      ['summary', 'Summary CSV', 'One row per page'],
      ['json', 'JSON', 'Everything, including preview lines'],
      ['text', 'Text report', 'Readable, for Slack or email'],
    ];
    const list = h('div', { role: 'menu', id: 'export-menu', 'aria-labelledby': 'export-trigger' }, items.map(([k, label, sub]) => {
      const it = h('div', { role: 'menuitem', id: `export-${k}`, class: 'menu-item' }, h('span', null, label), h('small', null, sub));
      it.addEventListener('click', () => exportAs(k));
      return it;
    }));
    const el = h('div', { class: 'dropdown-menu menu' }, trigger, h('div', { 'data-popover': true, 'data-align': 'end', 'aria-hidden': 'true' }, list));
    return { el, setEnabled: (on: boolean) => { trigger.disabled = !on; trigger.title = on ? '' : 'Available when the check finishes'; } };
  }

  onSettings(() => {
    if (!run) return;
    const same = run.checker === state.checker && run.profiles.map(p => p.id).join() === enabledProfiles().map(p => p.id).join() && run.brand === state.settings.brand;
    rerunBtn.hidden = same || !run.done;
    clear(staleNote, same ? null : alert('Settings changed since this check ran. Results and exports still use the old settings until you re-run.'));
  });

  renderTop();
  return root;
}
